import { useCallback } from 'react';
import { mergeChildResults, type ChildResult } from '@atlassian/jira-command-palette';
import { useNavigationViewPlansPermission } from '@atlassian/jira-plans-item-visibility';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { QuickSearchResponseData } from '../../../../common/types';
import { useGetErrorChildResult } from '../../../../common/ui/error';
import { useGetRecommendedChildResult } from '../../../../common/ui/recommended';
import { useSetTimeoutAutoClear } from '../../../../common/utils';
import { transformResultsForAnalytics } from '../../../../controllers/use-quick-search-analytics/utils';
import { useGetSearchBoardsChildResult } from './boards';
import { useGetSearchFiltersChildResult } from './filters';
import { useGetSearchIssuesChildResult } from './issues';
import { useGetSearchPlansChildResult } from './plans';
import { useGetSearchProjectsChildResult } from './projects';

export const useGetSearchResults = () => {
	const { getChildResult: getErrorChildResult } = useGetErrorChildResult();
	const { getChildResult: getRecommendedChildResult } = useGetRecommendedChildResult();
	const { getChildResult: getSearchIssuesChildResult } = useGetSearchIssuesChildResult();
	const { getChildResult: getSearchProjectsChildResult } = useGetSearchProjectsChildResult();
	const { getChildResult: getSearchBoardsChildResult } = useGetSearchBoardsChildResult();
	const { getChildResult: getSearchFiltersChildResult } = useGetSearchFiltersChildResult();
	const { getChildResult: getSearchPlansChildResult } = useGetSearchPlansChildResult();

	const [hasEditOrViewPermissions] = useNavigationViewPlansPermission();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { setTimeoutAutoClear } = useSetTimeoutAutoClear();

	const setTimeoutFunc = setTimeoutAutoClear;

	const getSearchResults = useCallback(
		({
			search,
			quickSearchResponseData,
			error,
		}: {
			search: string;
			quickSearchResponseData?: QuickSearchResponseData;
			error?: Error;
		}): ChildResult => {
			setTimeoutFunc(() => {
				fireUIAnalytics(createAnalyticsEvent({}), 'searchResults shown', 'postQuerySearchResults', {
					...transformResultsForAnalytics(quickSearchResponseData, false),
				});
			}, 0);

			return mergeChildResults([
				getErrorChildResult({ search, quickSearchResponseData, error }),
				getRecommendedChildResult({ search, quickSearchResponseData, error }),
				getSearchIssuesChildResult({ search, quickSearchResponseData, error }),
				getSearchProjectsChildResult({ search, quickSearchResponseData, error }),
				getSearchBoardsChildResult({ search, quickSearchResponseData, error }),
				getSearchFiltersChildResult({ search, quickSearchResponseData, error }),
				...(hasEditOrViewPermissions
					? [getSearchPlansChildResult({ search, quickSearchResponseData, error })]
					: []),
			]);
		},
		[
			createAnalyticsEvent,
			getErrorChildResult,
			getRecommendedChildResult,
			getSearchBoardsChildResult,
			getSearchFiltersChildResult,
			getSearchIssuesChildResult,
			getSearchPlansChildResult,
			getSearchProjectsChildResult,
			hasEditOrViewPermissions,
			setTimeoutFunc,
		],
	);

	return getSearchResults;
};
